import { KatalComponent } from '../../shared/base';

/**
 * @component {kat-expander} KatalExpander Expanders allow content to be hidden and broken into several collapsed sections. Customers have the ability to see one or more sections at a time. Expanders display introductory text with the rest hidden or collapsed.
 * @example SampleExpander {"label": "My Expander", "content": "<div>My Content to Hide</div>"}
 * @example WithBadge {"label": "My Expander", "badge-label": "New", "content": "<div>My Content to Hide</div>"}
 * @guideline Do Content must be valid html <div>Hello World</div>
 * @guideline Do Use expandable sections beneath relevant items in the interface to reduce page length.
 * @guideline Do Only include non-actionable and secondary content in the collapsed area of the expandable sections.
 * @guideline Do Use sentence case (not title case)
 * @guideline Do Keep heading content short and actionable by avoiding articles (such as "a," "an," "the")
 * @guideline Dont Don’t hide error messages or critical information under an expandable section.
 * @guideline Dont Don't use long text for badge labels, badge label has a max width of 150px.
 * @event expanded Expanded event will return true or false
 * @contentProvided
 * @htmlExample - <div>My Content here</div>
 * @status Production
 * @theme flo
 * @category Container
 */
export class KatalExpander extends KatalComponent {
  static get observedAttributes() {
    return ['label', 'expanded', 'badge-label', 'badge-type'];
  }

  constructor() {
    super(KatalExpander.observedAttributes);
  }

  connectedCallback() {
    super.connectedCallback();

    this._observer = new MutationObserver(mutations =>
      this.domChange(mutations)
    );
    this._observer.observe(this, { childList: true });

    this.addContentClass();
    this.generateHeader();
    this.prepend(this._header);
    this.setupToggle();
    this.render();
  }

  disconnectedCallback() {
    this._observer.disconnect();
  }

  domChange(mutations) {
    // We only want to respond to changes at the parent component.
    // This will cause a redraw of the current tabs.
    const validChanges = mutations.filter(m => m.target === this);
    if (validChanges) {
      this.addContentClass();
      // Make sure the collapsed classes are added to any new children if necessary
      this.toggleChildren(!this.expanded);
    }
  }

  addContentClass() {
    this.childNodes.forEach(c => {
      if (c === this._header) {
        return;
      }

      if (c.classList) {
        c.classList.toggle('kat-expander-content', true);
      }
    });
  }

  generateHeader() {
    this._label = document.createElement('span');
    this._label.classList.add('kat-header-text-label');

    this._toggle = document.createElement('div');
    this._toggle.classList.add('kat-expander-toggle');
    this._toggle.classList.add('kat-expander-arrow-down');

    this._badge = document.createElement('kat-badge');

    this._badgeContainer = document.createElement('span');
    this._badgeContainer.classList.add('kat-badge-container');
    this._badgeContainer.appendChild(this._badge);

    this._textContainer = document.createElement('div');
    this._textContainer.classList.add('kat-header-text');
    this._textContainer.appendChild(this._label);
    this._textContainer.appendChild(this._badgeContainer);

    this._button = document.createElement('button');
    // prevent the button from causing form submissions
    this._button.setAttribute('type', 'button');
    this._button.appendChild(this._textContainer);
    this._button.appendChild(this._toggle);

    this._header = document.createElement('div');
    this._header.classList.add('kat-expander-header');
    this._header.appendChild(this._button);
  }

  setupToggle() {
    this.toggleExpander(this.expanded);
    this.querySelector('.kat-expander-header').addEventListener('click', () => {
      this.expanded = !this.expanded;
      this.expandedEvent();
    });
  }

  toggleExpander(expanded) {
    if (!this._toggle) {
      return;
    }
    this._button.setAttribute('aria-expanded', expanded);
    this._toggle.classList.toggle('kat-expander-arrow-right', !expanded);
    this._toggle.classList.toggle('kat-expander-arrow-down', expanded);
    this.toggleChildren(!expanded);
  }

  toggleChildren(collapsed) {
    this.childNodes.forEach(c => {
      if (c === this._header) {
        return;
      }
      if (c.classList) {
        c.classList.toggle('kat-expander-collapsed', collapsed);
      }
    });
  }

  expandedEvent() {
    this.dispatchCustomEvent('expanded', { expanded: this.expanded });
  }

  /**
   * @classprop {boolean} expanded Determines if the content should be displayed
   */
  get expanded() {
    return this.getBooleanAttribute('expanded');
  }

  set expanded(value) {
    this.setBooleanAttribute('expanded', value);
  }

  /**
   * @classprop {string} label Heading displayed for the expander
   */
  get label() {
    return this.getAttributeOrDefault('label', '');
  }

  set label(value) {
    this.setAttribute('label', value);
  }

  /**
   * @classprop {string} badge-label Text to display in an optional badge.
   */
  get badgeLabel() {
    return this.getAttributeOrDefault('badge-label', '');
  }

  set badgeLabel(value) {
    this.setAttribute('badge-label', value);
  }

  /**
   * @classprop {string} badge-type The type of the optional badge to display.
   */
  get badgeType() {
    return this.getAttributeOrDefault('badge-type', 'default');
  }

  set badgeType(value) {
    this.setAttribute('badge-type', value);
  }

  render() {
    if (this._label) {
      this._label.innerText = this.label;
    }

    if (this._badge) {
      const badgeLabel = this.badgeLabel;
      const badgeType = this.badgeType;
      if (badgeLabel) {
        this._badge.classList.remove('kat-badge-hidden');
      } else {
        this._badge.classList.add('kat-badge-hidden');
      }
      this._badge.label = badgeLabel;
      this._badge.type = badgeType;
    }
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr, oldValue, newValue) {
    if (oldValue != newValue) {
      this.render();
    }
    if (attr === 'expanded') {
      this.toggleExpander(this.expanded);
    }
  }
}

KatalComponent.register('kat-expander', KatalExpander);
